'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ErrorBoundary } from '@benzinga/core-ui';
import { menus, NavigationHeader, NavigationHeaderProps } from '@benzinga/navigation-ui';
import { disableOptinMonsterCampaigns } from '@benzinga/ads-utils';
import { HeaderProps } from '../../../src/entities/header';
import { applyChromeConfig } from '../../_libs/site-shell/getChromeConfig';
import {
  subscribePortfolioQuoteBar,
  type PortfolioQuoteBarDetail,
} from '../../_libs/site-shell/portfolioQuoteBar';
import '../../styles/header.scss';

export interface RootHeaderProps {
  headerProps: NavigationHeaderProps & HeaderProps;
}

const isPortfolioPath = (pathname: string | null) =>
  Boolean(pathname && (pathname === '/profile/portfolio' || pathname.startsWith('/profile/portfolio/')));

const RootHeader: React.FC<RootHeaderProps> = ({ headerProps }) => {
  const pathname = usePathname();
  const onPortfolio = isPortfolioPath(pathname);
  const chromeHeaderProps = useMemo(() => applyChromeConfig(pathname, headerProps), [headerProps, pathname]);
  const [portfolioQuoteBar, setPortfolioQuoteBar] = useState<PortfolioQuoteBarDetail | null>(null);

  useEffect(() => {
    document.addEventListener('om.Scripts.init', event => {
      if (event && event['detail']) {
        event['detail'].Scripts.enabled.fonts = false;
      }
    });

    disableOptinMonsterCampaigns();
  }, []);

  useEffect(() => {
    if (!onPortfolio) {
      setPortfolioQuoteBar(null);
      return;
    }

    return subscribePortfolioQuoteBar(detail => {
      setPortfolioQuoteBar(detail?.tickers?.length ? detail : null);
    });
  }, [onPortfolio]);

  // On portfolio never fall back to SPY/QQQ — empty until watchlist publishes, then swap in place.
  const marketTickers = onPortfolio ? (portfolioQuoteBar?.tickers ?? []) : chromeHeaderProps?.marketTickers;
  const quotes = useMemo(() => {
    if (!onPortfolio) return chromeHeaderProps?.quotes;
    if (!portfolioQuoteBar?.quotes) return null;
    return portfolioQuoteBar.quotes;
  }, [chromeHeaderProps?.quotes, onPortfolio, portfolioQuoteBar]);

  if (chromeHeaderProps?.hideNavigationBar) {
    return null;
  }

  return (
    <ErrorBoundary name="header">
      <NavigationHeader
        hideBanner={chromeHeaderProps?.hideBanner}
        hideMenuBar={chromeHeaderProps?.hideMenuBar}
        hideQuoteBar={chromeHeaderProps?.hideQuoteBar}
        hideSearchBar={chromeHeaderProps?.hideSearchBar}
        isMainDrawerVisible={chromeHeaderProps?.isMainDrawerVisible}
        initialBreakingsNews={chromeHeaderProps?.breakingNews}
        initialTrendingTickers={chromeHeaderProps?.initialTrendingTickers}
        initialTrendingTopics={chromeHeaderProps?.initialTrendingTopics}
        logoVariant={chromeHeaderProps?.navigationLogoVariant || 'default'}
        marketTickers={marketTickers}
        menus={chromeHeaderProps?.menus ?? menus}
        primaryQuotesLabel={onPortfolio ? 'Watchlist' : undefined}
        quotes={quotes}
        showAboveHeaderBlock={false}
        raptiveExpandedHeader={chromeHeaderProps?.raptiveExpandedHeader}
        showRaptiveBanner={chromeHeaderProps?.showRaptiveBanner ?? true}
        showRotatingBanner={
          typeof chromeHeaderProps?.showRotatingBanner === 'boolean' ? chromeHeaderProps.showRotatingBanner : false
        }
        sticky={chromeHeaderProps?.sticky}
        variant={chromeHeaderProps?.variant ?? 'default'}
      />
    </ErrorBoundary>
  );
};

export default RootHeader;
